import {
  Alert,
  Box,
  Button,
  Center,
  Group,
  Paper,
  PasswordInput,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
  ThemeIcon,
  Title,
} from "@mantine/core";
import { type FormEvent, useState } from "react";

import { ApiError } from "../api/fetcher";
import { useVersionInfo } from "../api/hooks";
import { useAuth } from "../auth";
import { IconFileSpreadsheet, IconRadar, IconScale } from "../components/icons";
import { Logo } from "../components/Logo";

const POINTS = [
  { icon: IconRadar, text: "Lidar-measured volume and tonnes for every pile, every sweep" },
  { icon: IconScale, text: "Stacked, reclaimed and settled material, attributed per sweep" },
  { icon: IconFileSpreadsheet, text: "Audit-ready shift and daily reports in Excel and PDF" },
];

export function Login() {
  const { login, loggingIn, loginError } = useAuth();
  const version = useVersionInfo();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const submit = (e: FormEvent) => {
    e.preventDefault();
    void login(username, password).catch(() => undefined);
  };
  const message =
    loginError instanceof ApiError
      ? loginError.status === 429
        ? "Too many attempts. Wait a minute."
        : "Wrong username or password."
      : loginError
        ? "The server cannot be reached."
        : null;
  const v = version.data;
  return (
    <SimpleGrid cols={{ base: 1, md: 2 }} spacing={0} mih="100vh">
      <Box className="sv-login-brand" visibleFrom="md" p={56}>
        <Stack justify="space-between" h="100%">
          <Logo size={36} tagline="Stockpile monitoring" />
          <Stack gap="xl" maw={460}>
            <Text fz={34} fw={650} lh={1.15} c="#fff" style={{ letterSpacing: "-0.02em" }}>
              Stockpile volumes, measured every sweep.
            </Text>
            <Stack gap="md">
              {POINTS.map(({ icon: Ico, text }) => (
                <Group key={text} gap="md" wrap="nowrap">
                  <ThemeIcon size={36} radius="md" variant="light" color="brand">
                    <Ico size={20} />
                  </ThemeIcon>
                  <Text size="sm" c="#C2CFDC">
                    {text}
                  </Text>
                </Group>
              ))}
            </Stack>
          </Stack>
          <Text size="xs" c="#7E92A6">
            Ouster OS1 lidar on the stacker boom · processed on site
          </Text>
        </Stack>
      </Box>
      <Center p="xl" style={{ background: "var(--sv-canvas)" }}>
        <Stack w={380} maw="100%" gap="lg">
          <Box hiddenFrom="md">
            <Logo size={32} tone="onLight" tagline="Stockpile monitoring" />
          </Box>
          <Paper p="xl" shadow="md" withBorder>
            <form onSubmit={submit}>
              <Stack>
                <Stack gap={4}>
                  <Title order={2}>Sign in</Title>
                  <Text size="sm" c="dimmed">
                    Use the account your administrator gave you.
                  </Text>
                </Stack>
                <TextInput
                  label="Username"
                  value={username}
                  onChange={(e) => setUsername(e.currentTarget.value)}
                  autoComplete="username"
                  autoFocus
                  required
                />
                <PasswordInput
                  label="Password"
                  value={password}
                  onChange={(e) => setPassword(e.currentTarget.value)}
                  autoComplete="current-password"
                  required
                />
                {message && <Alert color="red">{message}</Alert>}
                <Button type="submit" loading={loggingIn} fullWidth mt="xs">
                  Log in
                </Button>
              </Stack>
            </form>
          </Paper>
          {v?.version && (
            <Text size="xs" c="dimmed" ta="center">
              StockVol {v.version}
              {v.git_sha ? ` · ${v.git_sha.slice(0, 7)}` : ""}
            </Text>
          )}
        </Stack>
      </Center>
    </SimpleGrid>
  );
}
